import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from './index'
import type { IClientUID } from '../clients'

export enum SystemInfoFragment {
    CommonSystemInfo = 0,
    SodaUnit = 1,
    AluminiumBlockCoolingUnit = 2,
    IceBankCoolingUnit = 3,
    AmbientUnit = 4,
    CirculationPumpUnitUnit = 5,
    HeatingUnit = 6,
    RelaySolenoids = 7,
    ShabbatInfo = 8,
    FilterFiller = 9,

    Last = 10,
}

export interface IFragmentedSystemInfo {
    [fragmentVersion: number]: IFragmentedSystemInfo_v1 | IFragmentedSystemInfo_v2 | IFragmentedSystemInfo_v3

    1: IFragmentedSystemInfo_v1
    2: IFragmentedSystemInfo_v2
    3: IFragmentedSystemInfo_v3
}

export interface IFragmentedSystemInfo_v1 {
    common?: ICommonSystemInfo_v1
    sodaUnit?: ISodaUnit_v1
    aluBlockCoolingUnit?: IAluBlockCoolingUnit_v1
    iceBlockCoolingUnit?: IIceBankUnit_v1
    ambientUnit?: IAmbientUnit_v1
    circulationPumpUnit?: ICirculationPumpUnit_v1
    heatingUnit?: IHeatingUnit_v1
    relaySolenoids?: IRelaySolenoids_v1
    filterFiller?: IFilterFiller_v1
    shabbatInfo?: IShabbatInfo_v1
}

export type IFragmentedSystemInfo_v2 = Omit<IFragmentedSystemInfo_v1, 'iceBlockCoolingUnit'> & {
    iceBlockCoolingUnit?: IIceBankUnit_v2
}

export type IFragmentedSystemInfo_v3 = Omit<IFragmentedSystemInfo_v1, 'iceBlockCoolingUnit'> & {
    iceBlockCoolingUnit?: IIceBankUnit_v3
}

interface ICommonSystemInfo_v1 {
    epoch: number
    uptime: number
    freeHeap: number
    ambient: number
    rtcBatteryInvalid?: number
}

interface ISodaUnit_v1 {
    pumpState: number
    oneLiquidLevel: number
    higherSensor: number
    lowerSensor: number
    ignoreH2oPiezo: number
    piezo1: number
    ignoreCo2Piezo: number
    piezo2: number
    pumpActiveTime: number
    pumpInactiveTime: number
}

interface IAluBlockCoolingUnit_v1 {
    compressorState: number
    ntc1: number
    compressorActiveTime: number
    compressorInactiveTime: number
}

interface IIceBankUnit_v1 {
    compressorState: number
    ntc1: number
    compressorActiveTime: number
    compressorInactiveTime: number
    iceSenseAvg: number
    iceSenseRaw: number
}

interface IIceBankUnit_v2 {
    compressorState?: number
    ntc1?: number
    compressorActiveTime?: number
    compressorInactiveTime?: number
    iceSenseAvg?: number
    iceSenseRaw?: number
    maxWorkTimeMinutes: number
    retryWaitMinutes: number
}

interface IIceBankUnit_v3 {
    compressorState?: number
    ntc1?: number
    compressorActiveTime?: number
    compressorInactiveTime?: number
    iceSenseAvg?: number
    iceSenseRaw?: number
    icebankState: number
}

interface IAmbientUnit_v1 {
    state: number
    ambient: number
    fanActiveTime: number
    fanInactiveTime: number
}

interface ICirculationPumpUnit_v1 {
    state: number
    circPumpActiveTime: number
    circPumpInactiveTime: number
}

interface IHeatingUnit_v1 {
    boilerState: number
    adc: number
    boilerActiveTime: number
    boilerInactiveTime: number
}

interface IRelaySolenoids_v1 {
    relSol1State?: boolean
    relSol2State?: boolean
    relSol3State?: boolean
    relSol4State?: boolean
    relSol5State?: boolean
    relSol6State?: boolean
    relSol7State?: boolean
    relSol8State?: boolean
}

interface IFilterFiller_v1 {
    state: number
    counter: number
    filterFillerActiveTime: number
    filterFillerInactiveTime: number
}

interface IShabbatInfo_v1 {
    shabbatSettingEnabled: number
    shabbatActive: number
}

export async function getClientFragmentedSystemInfo(clientId: IClientUID): Promise<ApiResponse<IFragmentedSystemInfo>> {
    const response = await fetch(`${HOST_URL}fragmented-system-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<IFragmentedSystemInfo>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
